import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { Button } from '../ui/button'
import { Textarea } from '../ui/controls'
import { toast } from 'sonner'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import './CopilotWidget.css'
import { X, Headphones, Bot, Send, Loader2 } from 'lucide-react'
import {
  CopilotMessage,
  getCopilotConfig,
  sendCopilotMessage,
} from '../../services/copilot'

type ChatMessage = {
  id: string
  role: 'user' | 'assistant'
  content: string
  status?: 'sending' | 'error'
}

const initialMessage: ChatMessage = {
  id: 'welcome',
  role: 'assistant',
  content:
    '你好，我是 Kyren Copilot。你可以问我控制台操作、API 对接，或当前账户可用支付方式和费率。',
}

export default function CopilotWidget() {
  const [enabled, setEnabled] = useState(false)
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([initialMessage])
  const listRef = useRef<HTMLDivElement>(null)
  const [viewport, setViewport] = useState<CSSProperties>({})

  useEffect(() => {
    if (!open) {
      setViewport({})
      return
    }
    if (!window.visualViewport) return
    const visibleViewport = window.visualViewport
    const sync = () => setViewport({
      '--copilot-viewport-height': `${visibleViewport.height}px`,
      '--copilot-keyboard-inset': `${Math.max(0, window.innerHeight - visibleViewport.height - visibleViewport.offsetTop)}px`,
    } as CSSProperties)
    sync()
    visibleViewport.addEventListener('resize', sync)
    visibleViewport.addEventListener('scroll', sync)
    return () => {
      visibleViewport.removeEventListener('resize', sync)
      visibleViewport.removeEventListener('scroll', sync)
    }
  }, [open])

  const conversation = useMemo(
    () =>
      messages
        .filter(
          (item): item is ChatMessage & CopilotMessage =>
            item.id !== 'welcome' &&
            item.role === 'user' &&
            item.status !== 'error',
        )
        .map(({ role, content }) => ({ role, content })),
    [messages],
  )

  useEffect(() => {
    getCopilotConfig()
      .then((res) => setEnabled(res.data.enabled))
      .catch(() => setEnabled(false))
  }, [])

  useEffect(() => {
    if (!open) return
    listRef.current?.scrollTo({
      top: listRef.current.scrollHeight,
      behavior: 'smooth',
    })
  }, [messages, open])

  const sendMessage = async () => {
    const content = input.trim()
    if (!content || sending) return

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
    }
    setMessages((items) => [...items, userMessage])
    setInput('')
    setSending(true)

    try {
      const latestPayloadMessage: CopilotMessage = { role: 'user', content }
      const payload: CopilotMessage[] = [
        ...conversation,
        latestPayloadMessage,
      ].slice(-10)
      const res = await sendCopilotMessage(payload, detectLanguage(content))
      const answer: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: res.data.answer,
      }
      setMessages((items) => [...items, answer])
    } catch (error) {
      const fallback: ChatMessage = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        status: 'error',
        content:
          error instanceof Error
            ? error.message
            : 'Copilot 暫時無法回覆，請稍後再試。',
      }
      setMessages((items) => [...items, fallback])
      toast.error('Copilot 暫時無法回覆')
    } finally {
      setSending(false)
    }
  }

  if (!enabled) return null

  return (
    <div className="copilot-widget" style={viewport}>
      {open && (
        <section className="copilot-panel" aria-label="Kyren Copilot">
          <header className="copilot-header">
            <div className="copilot-title">
              <Bot size={17} />
              <span>Kyren Copilot</span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setOpen(false)}
              aria-label="Close Copilot"
            >
              <X size={16} />
            </Button>
          </header>

          <div className="copilot-messages" ref={listRef}>
            {messages.map((item) => (
              <div
                key={item.id}
                className={`copilot-message copilot-message-${item.role}${item.status === 'error' ? ' copilot-message-error' : ''}`}
              >
                {item.role === 'assistant' ? (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      a: ({ node: _node, ...props }) => (
                        <a
                          {...props}
                          target="_blank"
                          rel="noreferrer noopener"
                        />
                      ),
                    }}
                  >
                    {item.content}
                  </ReactMarkdown>
                ) : (
                  item.content
                )}
              </div>
            ))}
            {sending && (
              <div className="copilot-message copilot-message-assistant copilot-message-pending">
                正在整理答案...
              </div>
            )}
          </div>

          <div className="copilot-input">
            <div className="copilot-composer">
              <Textarea
                value={input}
                rows={2}
                maxLength={1000}
                placeholder="Ask about your account, rates, or API integration"
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={(event) => {
                  if (
                    event.key === 'Enter' &&
                    !event.shiftKey &&
                    !event.nativeEvent.isComposing
                  ) {
                    event.preventDefault()
                    void sendMessage()
                  }
                }}
                disabled={sending}
              />
              <Button
                onClick={() => void sendMessage()}
                disabled={sending}
                aria-label="Send Copilot message"
                className="copilot-send"
              >
                {sending ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Send size={16} />
                )}
              </Button>
            </div>
          </div>
        </section>
      )}

      <Button
        className="copilot-fab size-12 rounded-full shadow-lg"
        onClick={() => setOpen((value) => !value)}
        aria-label={open ? 'Close Kyren Copilot' : 'Open Kyren Copilot'}
      >
        <Headphones size={22} />
      </Button>
    </div>
  )
}

function detectLanguage(content: string): 'en' | 'zh' | 'zh-Hant' {
  if (!/[\u4e00-\u9fff]/.test(content)) return 'en'
  if (/[體臺灣粵門請問資開啟關閉應該匯費結嗎]/.test(content)) return 'zh-Hant'
  if (/[体台湾粤请问资料开启关闭应该汇款费率结算吗]/.test(content)) return 'zh'
  return 'zh'
}
